import {
  ArrowDownWideNarrow,
  CalendarRange,
  ChevronDown,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui'
import { formatChartValue } from '@/components/ui/chart/utils/formatters'
import { formatCalendarDate } from '@/utils'
import { isGraphSpec } from './chart/filters'
import type {
  ChartSpec,
  DataRange,
  DateBounds,
  ExpandedFilters,
} from './chart/types'

interface ChartFilterBarProps {
  spec: ChartSpec
  filters: ExpandedFilters
  dataRange: DataRange
  dateBounds: DateBounds | null
  shownCount: number
  totalCount: number
  onChange: (patch: Partial<ExpandedFilters>) => void
  onReset: () => void
}

const TOP_N_PRESETS = [5, 10, 15, 20, 25, 30]
const CONTROL_CLASS =
  'h-8 rounded-md border bg-background text-sm text-foreground outline-none transition-colors focus-visible:ring-1 focus-visible:ring-ring'

export default function ChartFilterBar({
  spec,
  filters,
  dataRange,
  dateBounds,
  shownCount,
  totalCount,
  onChange,
  onReset,
}: ChartFilterBarProps) {
  const isGraph = isGraphSpec(spec)
  const itemNoun = isGraph ? 'flows' : 'points'
  const valueFormat = spec.valueFormat ?? 'currency'
  const formatValue = (value: number) => formatChartValue(value, valueFormat)

  const step = sliderStep(dataRange.max - dataRange.min)
  const sliderDisabled = dataRange.max <= dataRange.min
  const minValue = Math.max(
    dataRange.min,
    Math.min(filters.minValue, dataRange.max),
  )

  const topPresets = TOP_N_PRESETS.filter((preset) => preset < totalCount)
  const showTopN = topPresets.length > 0

  const chips = activeFilterChips({
    filters,
    dataRange,
    formatValue,
    onChange,
  })

  return (
    <div className="flex shrink-0 flex-col gap-2.5 border-b bg-muted/30 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5 text-sm">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="text"
            value={filters.labelQuery}
            onChange={(event) => onChange({ labelQuery: event.target.value })}
            placeholder={`Search ${itemNoun}`}
            aria-label={`Search ${itemNoun}`}
            className={`w-48 pl-8 pr-7 ${CONTROL_CLASS}`}
          />
          {filters.labelQuery && (
            <button
              type="button"
              onClick={() => onChange({ labelQuery: '' })}
              aria-label="Clear search"
              className="absolute right-1.5 top-1/2 grid size-5 -translate-y-1/2 cursor-pointer place-items-center rounded text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          )}
        </div>

        <Divider />

        <div className="flex items-center gap-2.5">
          <SlidersHorizontal
            className="size-3.5 text-muted-foreground"
            aria-hidden
          />
          <input
            type="range"
            min={dataRange.min}
            max={dataRange.max}
            step={step}
            value={minValue}
            onChange={(event) =>
              onChange({ minValue: Math.max(0, Number(event.target.value)) })
            }
            disabled={sliderDisabled}
            title={`${formatValue(dataRange.min)} – ${formatValue(dataRange.max)}`}
            aria-label="Hide values below"
            className="h-2 w-44 cursor-pointer accent-foreground disabled:cursor-not-allowed disabled:opacity-50"
          />
          <span className="min-w-[5ch] rounded-md border bg-background px-1.5 py-0.5 text-center text-xs tabular-nums text-foreground">
            ≥ {formatValue(minValue)}
          </span>
        </div>

        {showTopN && (
          <>
            <Divider />
            <label className="flex items-center gap-2 text-muted-foreground">
              <ArrowDownWideNarrow className="size-3.5" aria-hidden />
              <span>Top</span>
              <span className="relative">
                <select
                  value={filters.topN ?? 'all'}
                  onChange={(event) =>
                    onChange({
                      topN:
                        event.target.value === 'all'
                          ? null
                          : Number(event.target.value),
                    })
                  }
                  aria-label="Show only the top N by value"
                  className={`cursor-pointer appearance-none pl-2.5 pr-7 ${CONTROL_CLASS}`}
                >
                  <option value="all">All</option>
                  {topPresets.map((preset) => (
                    <option key={preset} value={preset}>
                      {preset}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
              </span>
            </label>
          </>
        )}

        {dateBounds && (
          <>
            <Divider />
            <div className="flex items-center gap-2 text-muted-foreground">
              <CalendarRange className="size-3.5" aria-hidden />
              <input
                type="date"
                min={dateBounds.min}
                max={dateBounds.max}
                value={filters.dateStart ?? ''}
                onChange={(event) =>
                  onChange({ dateStart: event.target.value || null })
                }
                aria-label="From date"
                className={`cursor-pointer px-2 ${CONTROL_CLASS}`}
              />
              <span>to</span>
              <input
                type="date"
                min={dateBounds.min}
                max={dateBounds.max}
                value={filters.dateEnd ?? ''}
                onChange={(event) =>
                  onChange({ dateEnd: event.target.value || null })
                }
                aria-label="To date"
                className={`cursor-pointer px-2 ${CONTROL_CLASS}`}
              />
            </div>
          </>
        )}

        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs tabular-nums text-muted-foreground">
            {shownCount === totalCount
              ? `${totalCount} ${itemNoun}`
              : `${shownCount} of ${totalCount} ${itemNoun}`}
          </span>
          {chips.length > 0 && (
            <Button type="button" theme="ghost" size="xs" onClick={onReset}>
              Clear all
            </Button>
          )}
        </div>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <FilterChip
              key={chip.key}
              label={chip.label}
              onRemove={chip.onRemove}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function Divider() {
  return <span className="hidden h-5 w-px bg-border sm:block" aria-hidden />
}

interface FilterChipProps {
  label: string
  onRemove: () => void
}

function FilterChip({ label, onRemove }: FilterChipProps) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border bg-background py-0.5 pl-2.5 pr-1 text-xs text-foreground">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove filter: ${label}`}
        className="grid size-4 cursor-pointer place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="size-3" aria-hidden />
      </button>
    </span>
  )
}

interface ChipModel {
  key: string
  label: string
  onRemove: () => void
}

interface ActiveChipArgs {
  filters: ExpandedFilters
  dataRange: DataRange
  formatValue: (value: number) => string
  onChange: (patch: Partial<ExpandedFilters>) => void
}

function activeFilterChips({
  filters,
  dataRange,
  formatValue,
  onChange,
}: ActiveChipArgs): ChipModel[] {
  const chips: ChipModel[] = []

  if (filters.labelQuery.trim() !== '') {
    chips.push({
      key: 'search',
      label: `"${filters.labelQuery.trim()}"`,
      onRemove: () => onChange({ labelQuery: '' }),
    })
  }

  if (filters.minValue > dataRange.min) {
    chips.push({
      key: 'min',
      label: `≥ ${formatValue(filters.minValue)}`,
      onRemove: () => onChange({ minValue: dataRange.min }),
    })
  }

  if (filters.topN !== null) {
    chips.push({
      key: 'top',
      label: `Top ${filters.topN}`,
      onRemove: () => onChange({ topN: null }),
    })
  }

  if (filters.dateStart || filters.dateEnd) {
    chips.push({
      key: 'date',
      label: dateRangeLabel(filters.dateStart, filters.dateEnd),
      onRemove: () => onChange({ dateStart: null, dateEnd: null }),
    })
  }

  return chips
}

function dateRangeLabel(start: string | null, end: string | null): string {
  if (start && end) {
    return `${formatCalendarDate(start)} – ${formatCalendarDate(end)}`
  }
  if (start) return `From ${formatCalendarDate(start)}`
  return `Through ${formatCalendarDate(end)}`
}

function sliderStep(spread: number): number {
  if (spread <= 0) return 1
  const raw = spread / 200
  if (raw < 1) return 1
  return Math.max(1, Math.round(raw))
}
